/**
 * Pure SVG area-chart geometry for the Insights tab.
 *
 * Requirement 13.9 forbids a third-party charting dependency, so the trend
 * charts are hand-rolled SVG `<path>` elements. This module holds the whole of
 * their geometry: it maps per-day values (Req 13.12 — one value per Metric_Day)
 * onto viewBox coordinates and smooths them by converting a uniform Catmull-Rom
 * spline through the points into cubic bezier segments, then closes the path
 * along the baseline so the area fills.
 *
 * Everything here is a pure function from `number[]` to numbers or a path
 * string: no DOM, no React, no colour. Sizing stays in unitless viewBox space
 * so the chart can scale with `width: 100%` and stay legible at 360 CSS px
 * (Req 13.11); fill and stroke come from `.insights-*` rules in
 * `packages/frontend/src/styles/theme.css`.
 */

/** Default viewBox width in user units. */
export const VIEWBOX_WIDTH = 100;

/** Default viewBox height in user units. The baseline sits at `height`. */
export const VIEWBOX_HEIGHT = 40;

/** Decimal places kept in emitted coordinates. */
const COORD_DECIMALS = 3;

/** A point in viewBox space. y grows downwards, as in SVG. */
export interface Point {
  x: number;
  y: number;
}

/** Sizing and scaling for one series. */
export interface AreaPathOptions {
  /** viewBox width in user units. Defaults to {@link VIEWBOX_WIDTH}. */
  width?: number;
  /** viewBox height in user units. Defaults to {@link VIEWBOX_HEIGHT}. */
  height?: number;
  /**
   * Value mapped to the top of the viewBox. Pass {@link seriesMax} over every
   * series in a chart so overlaid series share one y axis (Req 13.12). Defaults
   * to the maximum of `values`. A max of 0 (or less) puts every point on the
   * baseline.
   */
  max?: number;
}

function roundCoord(value: number): number {
  const factor = 10 ** COORD_DECIMALS;
  return Math.round(value * factor) / factor;
}

/** Formats a coordinate for a path string, dropping trailing zeros. */
function fmt(value: number): string {
  // `+` normalises -0 to 0 so a baseline coordinate never renders as "-0".
  return String(roundCoord(value) + 0);
}

/** Treats non-finite and negative counts as 0 — counts are never negative. */
function sanitize(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * Largest value across one or more series, used as a shared y-axis maximum.
 * Returns 0 when there is nothing to plot or every value is 0.
 */
export function seriesMax(series: readonly (readonly number[])[]): number {
  let max = 0;
  for (const values of series) {
    for (const value of values) {
      const clean = sanitize(value);
      if (clean > max) max = clean;
    }
  }
  return max;
}

/**
 * Maps per-day values to viewBox coordinates, one point per value in order.
 *
 * x is spread evenly from 0 to `width`, so the first point sits on the left
 * edge and the last on the right edge. A lone value is centred. y is measured
 * from the baseline at `height`: a value of `max` reaches y = 0 and a value of
 * 0 sits on the baseline, so an all-zero series is a flat line along it.
 */
export function toPoints(
  values: readonly number[],
  options: AreaPathOptions = {},
): Point[] {
  const {
    width = VIEWBOX_WIDTH,
    height = VIEWBOX_HEIGHT,
    max = seriesMax([values]),
  } = options;
  const count = values.length;
  if (count === 0) return [];

  return values.map((value, index) => {
    const x = count === 1 ? width / 2 : (index / (count - 1)) * width;
    const scaled =
      max > 0 ? height - (clamp(sanitize(value), 0, max) / max) * height : height;
    return { x: roundCoord(x), y: roundCoord(scaled) };
  });
}

/**
 * Smooths `points` into an open path: one `M` followed by one cubic bezier
 * segment per gap between neighbouring points.
 *
 * The control points are the uniform Catmull-Rom spline through the points
 * expressed as beziers — each interior point contributes one control point to
 * the segment on either side of it, derived from its two neighbours (the end
 * points stand in for their own missing neighbour). Because that conversion is
 * exact, **every segment ends on the next data point**, so the curve passes
 * through every value it plots and never invents a peak between two days.
 * Control points are clamped to the viewBox so an overshoot cannot escape it;
 * clamping moves no segment endpoint.
 */
export function smoothLinePath(
  points: readonly Point[],
  options: Pick<AreaPathOptions, "width" | "height"> = {},
): string {
  const { width = VIEWBOX_WIDTH, height = VIEWBOX_HEIGHT } = options;
  if (points.length === 0) return "";

  const first = points[0];
  let d = `M ${fmt(first.x)},${fmt(first.y)}`;

  for (let i = 0; i < points.length - 1; i += 1) {
    const previous = points[i - 1] ?? points[i];
    const start = points[i];
    const end = points[i + 1];
    const next = points[i + 2] ?? points[i + 1];

    const c1x = clamp(start.x + (end.x - previous.x) / 6, 0, width);
    const c1y = clamp(start.y + (end.y - previous.y) / 6, 0, height);
    const c2x = clamp(end.x - (next.x - start.x) / 6, 0, width);
    const c2y = clamp(end.y - (next.y - start.y) / 6, 0, height);

    d += ` C ${fmt(c1x)},${fmt(c1y)} ${fmt(c2x)},${fmt(c2y)} ${fmt(end.x)},${fmt(end.y)}`;
  }

  return d;
}

/**
 * Builds a closed, fillable area path for one series of per-day values.
 *
 * The top edge is the smoothed curve from {@link smoothLinePath}; the path then
 * drops to the baseline under the last point, runs back along the baseline to
 * under the first point and closes with `Z`, so the enclosed area can be
 * filled from CSS. A single value is drawn as a full-width band at its own
 * height, because a zero-width area would fill nothing. An empty series has no
 * geometry and yields `""`.
 */
export function areaPath(
  values: readonly number[],
  options: AreaPathOptions = {},
): string {
  const { width = VIEWBOX_WIDTH, height = VIEWBOX_HEIGHT } = options;
  const points = toPoints(values, options);
  if (points.length === 0) return "";

  const baseline = fmt(height);

  if (points.length === 1) {
    const y = fmt(points[0].y);
    return `M ${fmt(0)},${y} L ${fmt(width)},${y} L ${fmt(width)},${baseline} L ${fmt(0)},${baseline} Z`;
  }

  const line = smoothLinePath(points, { width, height });
  const start = points[0];
  const end = points[points.length - 1];
  return `${line} L ${fmt(end.x)},${baseline} L ${fmt(start.x)},${baseline} Z`;
}
